import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe, LogOut } from "lucide-react";
import Logo from "@/components/Logo";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { HOME } from "@/constants/testIds";

export default function Navbar() {
  const { lang, setLang, t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const goToSection = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", "/#" + id);
    } else {
      navigate("/#" + id);
    }
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <Logo size={30} />
          <span className="font-display font-black tracking-tight text-lg">ViralCrew</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm text-zinc-300">
          <a href="/#features" onClick={goToSection("features")} className="hover:text-white transition" data-testid="nav-link-features">{t.nav.features}</a>
          <a href="/#pricing" onClick={goToSection("pricing")} className="hover:text-white transition" data-testid="nav-link-pricing">{t.nav.pricing}</a>
          <a href="/#faq" onClick={goToSection("faq")} className="hover:text-white transition" data-testid="nav-link-faq">{t.nav.faq}</a>
          <a href="/#contact" onClick={goToSection("contact")} className="hover:text-white transition" data-testid="nav-link-contact">{t.nav.contact}</a>
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid={HOME.langToggle}
            onClick={() => setLang(lang === "de" ? "en" : "de")}
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang.toUpperCase()}
          </button>

          {user ? (
            <>
              <button
                data-testid={HOME.navDashboard}
                onClick={() => navigate("/dashboard")}
                className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-white border border-white/15 hover:border-white/40 rounded-full px-4 py-1.5 transition"
              >
                {t.nav.dashboard}
              </button>
              <button
                data-testid={HOME.navLogout}
                onClick={async () => { await logout(); navigate("/"); }}
                className="inline-flex items-center gap-1.5 text-sm bg-[#FF0050] hover:bg-[#E00046] text-white font-semibold rounded-full px-4 py-1.5 transition glow-pink"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">{t.nav.logout}</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={goToSection("pricing")}
                data-testid="nav-start-cta"
                className="text-sm bg-[#FF0050] hover:bg-[#E00046] text-white font-semibold rounded-full px-4 py-1.5 transition hover:shadow-[0_0_20px_rgba(255,0,80,0.4)]"
              >
                {t.nav.start}
              </button>
              <Link
                to="/login"
                data-testid={HOME.navLogin}
                className="hidden sm:inline-flex items-center text-sm text-white border border-white/15 hover:border-white/40 font-semibold rounded-full px-4 py-1.5 transition"
              >
                {t.nav.login}
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
